<script setup lang="ts">
import { HgButton } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationRaw,
  useRoute,
  useRouter,
} from 'vue-router';

import DirectoryLocationSearch from '@/components/DirectoryLocationSearch/DirectoryLocationSearch.vue';
import DirectorySearchPlane from '@/components/DirectorySearchPlane/DirectorySearchPlane.vue';
import { useDirectoryRouteFacets } from '@/composables/useDirectoryRouteFacets';
import { buildFacetQuery, toggleFacet } from '@/helpers/AgentDirectory/agentFacetRouteParams';
import { agentFocusChips } from '@/helpers/AgentServices/agentServiceChips';
import { isPlatformHg } from '@/helpers/Platform/platform';
import { useAgentProfilesStore } from '@/stores/agentProfiles';
import { AgentService, AgentSpecialization } from '@/types/agentProfile';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const agentProfilesStore = useAgentProfilesStore();
const routeFacets = useDirectoryRouteFacets();

const chips = agentFocusChips();
const isHomegate = isPlatformHg();

const isChipSelected = (key: string): boolean => {
  if (Object.values(AgentService).includes(key as AgentService)) {
    return routeFacets.value.services.includes(key as AgentService);
  }

  if (Object.values(AgentSpecialization).includes(key as AgentSpecialization)) {
    return routeFacets.value.specializations.includes(key as AgentSpecialization);
  }

  return false;
};

const handleChipClick = (key: string) => {
  const nextFacets = toggleFacet(routeFacets.value, key);
  const nextQuery: Record<string, unknown> = { ...route.query };

  delete nextQuery.page;
  delete nextQuery.services;
  delete nextQuery.specializations;

  router
    .push({
      params: route.params,
      query: { ...nextQuery, ...buildFacetQuery(nextFacets) },
    } as unknown as RouteLocationRaw)
    .catch((error) => {
      if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
        throw new Error(error);
      }
    });
};
</script>

<template>
  <DirectorySearchPlane activeType="agent">
    <template #locations>
      <DirectoryLocationSearch :store="agentProfilesStore" />
    </template>
    <template #filters>
      <fieldset :class="$style.fieldset" data-test="agent-filter-chips">
        <legend :class="$style.legend">{{ t('agentFocusText') }}</legend>
        <div :class="$style.chipsWrapper">
          <HgButton
            v-for="chip in chips"
            :key="chip.key"
            type="button"
            size="small"
            theme="ghost"
            :aria-pressed="isChipSelected(chip.key)"
            :class="{
              [$style.chipButton]: true,
              [$style.chipButtonSelectedHg]: isChipSelected(chip.key) && isHomegate,
              [$style.chipButtonSelectedIs24]: isChipSelected(chip.key) && !isHomegate,
            }"
            :data-test="`agent-filter-chip-${chip.key}`"
            @click="handleChipClick(chip.key)"
          >
            {{ t(chip.labelKey) }}
          </HgButton>
        </div>
      </fieldset>
    </template>
  </DirectorySearchPlane>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.fieldset {
  padding: 0;
  margin: 0;
  border: 0;
}

.legend {
  @include label;

  padding: 0;
  margin-bottom: $space-50;
}

.chipsWrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 7.59px;
}

// Specificity stays above HgButton's two-class theme rules, which otherwise
// paint Homegate chips blue and replace the selected fill on hover.
.chipsWrapper button.chipButton {
  @include body-3($color-grey-120);

  box-sizing: border-box;
  min-width: 0;
  height: 30.36px;
  padding: 3.8px 11.39px;
  color: $color-grey-120;
  white-space: nowrap;
  background-color: $color-white;
  border: 0.95px solid $color-grey-120;
  border-radius: 15.18px;

  &:hover,
  &:focus,
  &:active {
    color: $color-grey-120;
    background-color: $color-grey-30;
    border-color: $color-grey-120;
  }

  &:focus-visible {
    outline: 2px solid $color-grey-120;
    outline-offset: 2px;
  }
}

.chipsWrapper button.chipButton.chipButtonSelectedHg {
  &,
  &:hover,
  &:focus,
  &:active {
    color: var(--theme-color-secondary-text);
    background-color: var(--theme-color-secondary);
    border-color: var(--theme-color-secondary);
  }

  &:hover {
    background-color: var(--theme-color-secondary-hover);
    border-color: var(--theme-color-secondary-hover);
  }
}

.chipsWrapper button.chipButton.chipButtonSelectedIs24 {
  &,
  &:hover,
  &:focus,
  &:active {
    color: $color-white;
    background-color: $color-grey-80;
    border-color: $color-grey-80;
  }

  &:hover {
    background-color: $color-grey-90;
    border-color: $color-grey-90;
  }
}
</style>
